import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase/config';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { clearCart } from '../utils/cartUtils';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sign up function
  const signup = async (email, password, userData) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Create user document in Firestore (never allow client to set privileged fields)
      const userDoc = {
        uid: user.uid,
        email: email,
        first_name: userData?.first_name,
        last_name: userData?.last_name,
        contact_number: userData?.contact_number,
        ic_number: userData?.ic_number,
        gender: userData?.gender,
        birth_date: userData?.birth_date || userData?.date_of_birth,
        nationality: userData?.nationality || userData?.Nationality,
        school_member: userData?.school_member || false,
        role: 'Members',
        approved: true, // Auto-approve since Firebase handles verification
        created_time: new Date()
      };
      
      await setDoc(doc(db, 'users', user.uid), userDoc);
      
      toast.success('Account created successfully! You can now log in.');
      return userCredential;
    } catch (error) {
      toast.error(error.message);
      throw error;
    }
  };

  // Sign in function
  const signin = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      toast.success('Welcome back!');
      return userCredential;
    } catch (error) {
      // Don't show toast here - let the login component handle the error display
      console.error('Sign in error:', error);
      throw error;
    }
  };

  // Sign out function
  const logout = async () => {
    try {
      await signOut(auth);
      setUserProfile(null);
      
      // Clear cart on logout
      clearCart();
      
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error(error.message);
      throw error;
    }
  };

  // Change password function
  const changePassword = async (currentPassword, newPassword) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No user is currently signed in');
      }

      // Changing password for user

      // Re-authenticate user with current password
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, newPassword);

      toast.success('Password changed successfully!');
      return true;
    } catch (error) {
      console.error('AuthContext: Password change error:', {
        errorCode: error.code,
        errorMessage: error.message,
        fullError: error
      });

      // Handle specific Firebase auth errors with user-friendly messages
      let errorMessage = 'Failed to change password. Please try again.';
      
      switch (error.code) {
        case 'auth/wrong-password':
          errorMessage = 'Current password is incorrect. Please try again.';
          break;
        case 'auth/weak-password':
          errorMessage = 'New password is too weak. Please choose a stronger password.';
          break;
        case 'auth/requires-recent-login':
          errorMessage = 'Please log in again before changing your password.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your connection and try again.';
          break;
        default:
          errorMessage = error.message || errorMessage;
      }

      toast.error(errorMessage);
      throw error;
    }
  };

  // Get user profile from backend API
  const getUserProfile = async (uid) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        console.error('No authenticated user');
        return null;
      }
      
      const token = await user.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      
      const response = await fetch(`${apiUrl}/api/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.user || null;
      } else {
        console.error('Failed to fetch user profile:', response.status);
        return null;
      }
    } catch (error) {
      console.error('Error getting user profile:', error);
      return null;
    }
  };

  // Check if user is admin
  const isAdmin = () => {
    return userProfile?.role?.toLowerCase() === 'admin';
  };

  // Check if user is approved - always true since Firebase handles verification
  const isApproved = () => {
    return currentUser !== null; // If user is authenticated by Firebase, they're approved
  };

  // Check if user is a member (has paid membership)
  const isMember = () => {
    return userProfile?.membership_status === 'active';
  };

  // Get membership expiry date
  const getMembershipExpiry = () => {
    return userProfile?.membership_expiry;
  };

  // Refresh user profile from Firestore
  const refreshUserProfile = async () => {
    if (currentUser) {
      try {
        const profile = await getUserProfile(currentUser.uid);
        setUserProfile(profile);
        return profile;
      } catch (error) {
        console.error('Error refreshing user profile:', error);
        return null;
      }
    }
    return null;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      const handleAuthStateChange = async () => {
        try {
          if (user) {
            setCurrentUser(user);
            
            // Get user profile from Firestore
            const profile = await getUserProfile(user.uid);
            setUserProfile(profile);
          } else {
            setCurrentUser(null);
            setUserProfile(null);
          }
        } catch (error) {
          console.error('Error in auth state change:', error);
          // Set user profile to null if there's an error fetching it
          setUserProfile(null);
        } finally {
          setLoading(false);
        }
      };

      // Call the async function and handle any unhandled rejections
      handleAuthStateChange().catch(error => {
        console.error('Unhandled promise rejection in auth state change:', error);
        setUserProfile(null);
        setLoading(false);
      });
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    signup,
    signin,
    logout,
    changePassword,
    isAdmin,
    isApproved,
    isMember,
    getMembershipExpiry,
    refreshUserProfile,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
